import { FilterDocument } from '@griply/types';
import { defaultAllTasksFilterDocument, taskListFilters } from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterContextMenu } from 'shared/components/ui/filter-context-menu';
import { FormSelectTrigger } from 'shared/components/ui/form-select-trigger';
import { useFilterState } from 'shared/hooks/use-filter-state';
import { useOnFilterOption } from 'shared/hooks/use-on-filter';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { getActiveFilterLabels } from './get-active-filter-labels';
import * as Styled from './smart-list-filter-field.style';

export type SmartListFilterFieldProps = {
  filterDocument: FilterDocument;
  onChange: (filterDocument: FilterDocument) => void;
};

export const SmartListFilterField: React.FC<SmartListFilterFieldProps> = ({
  filterDocument,
  onChange,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const user = useUser();
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const tags = useSortedTags();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const weekStartsOn = getWeekStartsOn(user);

  const { filters, activeOptions } = useFilterState(filterDocument, {
    goals,
    lifeAreas,
    tags,
    dateFormat,
    weekStartsOn,
    defaultFilterDocument: defaultAllTasksFilterDocument,
    supportedFilters: taskListFilters,
    supportedGroupByModes: [],
    supportedSortingModes: [],
  });

  const onFilter = useOnFilterOption(
    filterDocument,
    defaultAllTasksFilterDocument,
    onChange,
  );

  const onReset = () => onChange({ ...filterDocument, filters: null });

  const filterLabels = getActiveFilterLabels({ activeOptions, filters, t });

  return (
    <>
      <FormSelectTrigger
        triggerRef={triggerRef}
        onClick={openMenu}
        isPlaceholder={!filterLabels.length}
      >
        <Styled.Label>
          {filterLabels.length
            ? filterLabels.join(', ')
            : t('general.labels.select')}
        </Styled.Label>
      </FormSelectTrigger>

      {menuOpen && (
        <FilterContextMenu
          referenceElement={triggerRef}
          onOutside={closeMenu}
          position="bottom-start"
          title={t('context-menus.filtering.task.title')}
          options={filters}
          activeOptions={activeOptions}
          onOption={onFilter}
          onReset={onReset}
        />
      )}
    </>
  );
};
